// The vehicle vocabulary, and the rule that decides whether a car is active.
//
// WHY THIS IS ITS OWN FILE, with no imports at all.
//
// The active rule is written down twice: in SQL, in vehicle_is_active_v1
// (db/migrations/0126), which is the authority and the thing that refuses; and
// here, so the edit dialogs can tell an operator what a save will do BEFORE they
// send it. Two copies of one rule drift, and the drift is invisible: the browser
// warns about a save the database would have allowed, or stays quiet before one
// it refuses. web/test/activeIsDerived.test.ts reads both and fails when they
// disagree, and that test can only import a module the bare node test runner can
// resolve, which means a module that imports nothing.
//
// utils/fleet.ts re-exports everything here, so nothing else has to know.

export type StatusOption = { code: string; label: string }
export type StatusTone = 'brand' | 'amber' | 'green' | 'red' | 'neutral'

export function humanise(code: string): string {
  return code
    .split('_')
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ')
}

// ---------------------------------------------------------------------------
// Lifecycle stage.
//
// THE ENUM VALUE STAYS 'active'. Only the label reads Onboarded. Luyanda's point
// on 1 September 2026 was that the word Active was doing two jobs on one screen:
// naming the stage a car has reached, and asserting that the car is on the road
// today. The second meaning is now computed, so the stage keeps the value every
// view and metric already filters on and loses the word. Renaming the enum to
// make one dialog read better is how an active-cars count silently changes.

export const LIFECYCLE_STATUS_OPTIONS: StatusOption[] = [
  { code: 'active', label: 'Onboarded' },
  { code: 'contracting', label: 'Contracting' },
  { code: 'offboarding', label: 'Offboarding' },
  { code: 'offboarded', label: 'Offboarded' },
  { code: 'written_off', label: 'Written off' },
]

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  LIFECYCLE_STATUS_OPTIONS.map((o) => [o.code, o.label]),
)

export function fleetStatusLabel(code: string | null | undefined): string {
  if (!code) return 'Unknown'
  return STATUS_LABELS[code] ?? humanise(code)
}

// Onboarded is green on Luyanda's instruction, 1 September 2026. It now shares a
// colour with the Active chip beside it, which is a real cost: the two chips
// answer different questions, and green on both invites reading the stage as the
// state. The row keeps them apart by order and by wording, Active first and the
// stage under it, and the inactive rows carry the reason in words.
const STATUS_TONES: Record<string, StatusTone> = {
  active: 'green',
  contracting: 'brand',
  offboarding: 'amber',
  offboarded: 'neutral',
  written_off: 'red',
}

export function fleetStatusTone(code: string | null | undefined): StatusTone {
  if (!code) return 'neutral'
  return STATUS_TONES[code] ?? 'neutral'
}

// ---------------------------------------------------------------------------
// Hold reasons. Plural since migration 0126: a car can be waiting on a licence
// disc AND sitting in a panel shop, and the single-value column let the second
// write erase the first, so cars came back with one problem fixed and the other
// forgotten. There is no 'none' option any more: an empty list is no hold.

export const HOLD_REASON_OPTIONS: StatusOption[] = [
  { code: 'licensing', label: 'Licensing' },
  { code: 'repairs_or_claim', label: 'In repairs / claim' },
]

const HOLD_REASON_LABELS: Record<string, string> = Object.fromEntries(
  HOLD_REASON_OPTIONS.map((o) => [o.code, o.label]),
)

export function holdReasonLabel(code: string): string {
  return HOLD_REASON_LABELS[code] ?? humanise(code)
}

export function holdReasonTone(code: string): StatusTone {
  return code === 'licensing' ? 'amber' : 'red'
}

/** One chip per reason. Empty array when the car is not on hold. */
export function holdReasonChips(
  reasons: string[] | null | undefined,
): { tone: StatusTone; label: string }[] {
  return (reasons ?? []).map((r) => ({ tone: holdReasonTone(r), label: holdReasonLabel(r) }))
}

// ---------------------------------------------------------------------------
// The six places a vehicle can be, as Luyanda set them on 1 September 2026.
// Migration 0126 moved the three that were in use onto this list and cleared the
// two one-off strings nobody could map. Everything here except Operating reads as
// inactive, which is the whole point of the list.
//
// IMPOUNDED WAS ADDED THE SAME DAY, on Luyanda's instruction, after the first
// five went in. The Licensing branch of the incident form asks where a vehicle
// has been taken, and a pound is not an office, a driver's house, a workshop or
// an insurance yard. Without it that branch had to write free text into a column
// every other screen treats as a closed list, which is how 'On Fleet
// Johannesburg' and 'not operating' got there in the first place.

export const LOCATION_OPERATING = 'Operating'

export const LOCATION_OPTIONS: string[] = [
  LOCATION_OPERATING,
  'At Office',
  'At Driver Home',
  'In Repairs',
  'Insurance Depot',
  'Impounded',
]

export function locationLabel(loc: string | null | undefined): string {
  return loc || 'Not recorded'
}

export function locationTone(loc: string | null | undefined): StatusTone {
  if (!loc) return 'neutral'
  switch (loc) {
    case 'Operating':
      return 'green'
    case 'At Office':
    case 'At Driver Home':
      return 'amber'
    case 'In Repairs':
    case 'Insurance Depot':
    case 'Impounded':
      return 'red'
    default:
      return 'neutral'
  }
}

// Buckets for the location filter and the funnel strip. Unknown carries both a
// blank location and any legacy value that survived the migration, because a car
// nobody has placed and a car sitting at a retired place name are the same
// problem: nobody knows where it is.
export const LOCATION_BUCKETS = [...LOCATION_OPTIONS, 'Unknown'] as const
export type LocationBucket = (typeof LOCATION_BUCKETS)[number]

export function locationBucket(loc: string | null | undefined): LocationBucket {
  if (loc && LOCATION_OPTIONS.includes(loc)) return loc as LocationBucket
  return 'Unknown'
}

// ---------------------------------------------------------------------------
// Active.

/**
 * The lifecycle stages that mean the car is no longer Fleetly's to run.
 *
 * Not one of the four conditions Luyanda listed, and added deliberately. Four
 * vehicles carry status offboarded with in_general_fleet still true, and without
 * this a car Fleetly no longer runs would read Active the moment somebody
 * recorded its location. The set matches migration 0124's scope exactly, so the
 * Expiries tab and the Fleet page agree about which cars are Fleetly's.
 *
 * 'offboarding' is deliberately absent: a car winding down is still carrying
 * fares and still earning against its drivers' guaranteed salaries.
 */
export const LEFT_THE_FLEET = ['sold', 'archived', 'offboarded', 'written_off']

export type ActiveInput = {
  lifecycle_status: string | null | undefined
  in_general_fleet: boolean | null | undefined
  hold_reasons: string[] | null | undefined
  operational: boolean | null | undefined
  location: string | null | undefined
}

export function isActive(input: ActiveInput): boolean {
  return (
    input.in_general_fleet === true &&
    !LEFT_THE_FLEET.includes(input.lifecycle_status ?? '') &&
    (input.hold_reasons ?? []).length === 0 &&
    input.operational === true &&
    input.location === LOCATION_OPERATING
  )
}

/**
 * Every condition that is failing, in the operator's words. Empty when the car
 * is active. All of them, never only the first: fixing one and finding the car
 * still inactive is how people stop trusting a screen.
 */
export function inactiveReasons(input: ActiveInput): string[] {
  const out: string[] = []
  if (input.in_general_fleet !== true) out.push('not in the general fleet')
  if (LEFT_THE_FLEET.includes(input.lifecycle_status ?? '')) {
    out.push(`lifecycle stage is ${fleetStatusLabel(input.lifecycle_status).toLowerCase()}`)
  }
  const holds = input.hold_reasons ?? []
  if (holds.length > 0) out.push(`on hold: ${holds.map(holdReasonLabel).join(' and ')}`)
  if (input.operational !== true) out.push('not operational')
  if (input.location !== LOCATION_OPERATING) {
    out.push(`location is ${input.location || 'not recorded'}`)
  }
  return out
}
